import type { CSSProperties } from 'react'
import DashIcon from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'
import StatusBadge from '../../components/ui/StatusBadge'

interface CalDay {
  n: number
  muted?: boolean
  today?: boolean
  dots?: string[]
}

function buildCalendar(): CalDay[] {
  const monthStart = new Date(2026, 4, 1)
  const startDay = (monthStart.getDay() + 6) % 7
  const days: CalDay[] = []
  for (let i = 0; i < startDay; i++) days.push({ n: 30 - startDay + i + 1, muted: true })
  for (let i = 1; i <= 31; i++) {
    let dots: string[] | undefined
    if (i === 17) dots = ['amber', 'green']
    else if (i === 19) dots = ['blue', 'blue', 'green']
    else if (i === 22) dots = ['green']
    else if (i === 24) dots = ['amber']
    days.push({ n: i, today: i === 16, dots })
  }
  return days
}

const days = buildCalendar()

const upcomingPickups = [
  ['17 mai 2026', '09:00–11:00', 'Av. H. Bourguiba, Tunis Centre', 18, 'Youssef Karoui', 'scheduled'],
  ['17 mai 2026', '15:00–17:00', 'Rue de Carthage, La Marsa',       8, 'Salma Mejri',   'scheduled'],
  ['19 mai 2026', '10:00–12:00', 'Lac 2, Tunis',                   24, 'Karim Hammami', 'scheduled'],
  ['19 mai 2026', '14:00–16:00', 'Av. Mohamed V, Tunis',           14, 'Auto-assign',   'pending'],
  ['22 mai 2026', '08:00–10:00', 'Centre Urbain Nord',              6, 'Auto-assign',   'pending'],
] as const

export default function ExpediteurPickup() {
  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Pickup &amp; Collectes</h1>
          <p>Planifiez les collectes de vos colis par nos livreurs.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost"><DashIcon name="receipt" size={14} /> Historique</button>
          <button className="btn btn-primary"><DashIcon name="plus" size={14} /> Demander un pickup</button>
        </div>
      </div>

      <div className="detail-grid" style={{ '--detail-cols': '1fr 380px', gap: 18, marginBottom: 18 } as CSSProperties}>
        <div className="card">
          <div className="card-head">
            <h3>Mai 2026</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm btn-ghost">←</button>
              <button className="btn btn-sm btn-ghost">Aujourd'hui</button>
              <button className="btn btn-sm btn-ghost">→</button>
            </div>
          </div>
          <div className="cal-grid">
            {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d => (
              <div key={d} className="cal-head">{d}</div>
            ))}
            {days.map((d, i) => (
              <div
                key={i}
                className={`cal-cell${d.muted ? ' muted' : ''}${d.today ? ' today' : ''}`}
              >
                {d.n}
                {d.dots && (
                  <div className="cal-dots">
                    {d.dots.map((c, j) => <i key={j} className={c === 'green' ? '' : c} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Aujourd'hui · 16 mai</h3>
            <span className="badge badge-green"><span className="pulse-dot" />2 prévus</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { time: '09:00 - 11:00', addr: 'Av. Habib Bourguiba, Tunis', courier: 'Youssef K.', count: 12, status: 'in_transit' },
              { time: '14:00 - 16:00', addr: 'Lac 2, Tunis',               courier: 'Salma M.',   count: 6,  status: 'scheduled' },
            ].map((p, i) => (
              <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <b className="mono" style={{ color: 'var(--navy-900)', fontSize: '.88rem' }}>{p.time}</b>
                  <StatusBadge status={p.status} />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10, fontSize: '.84rem', color: 'var(--navy-800)' }}>
                  <DashIcon name="pin" size={14} /><span>{p.addr}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="cell-user">
                    <DashAvatar name={p.courier} size={28} />
                    <div><b>{p.courier}</b><span>Livreur assigné</span></div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <b style={{ color: 'var(--navy-900)', fontFamily: 'Space Grotesk', fontSize: '1.1rem' }}>{p.count}</b>
                    <div style={{ color: 'var(--muted)', fontSize: '.74rem' }}>colis</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="tbl-card">
        <div className="tbl-toolbar">
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Prochaines collectes</h3>
          <div style={{ flex: 1 }} />
          <div className="seg">
            <button className="is-active">À venir</button>
            <button>Passées</button>
          </div>
        </div>
        <div className="tbl-scroll">
        <table className="tbl">
          <thead>
            <tr>
              <th>Date</th><th>Créneau</th><th>Adresse</th>
              <th>Colis</th><th>Livreur</th><th>Statut</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {upcomingPickups.map((r, i) => (
              <tr key={i}>
                <td className="row-strong">{r[0]}</td>
                <td className="mono">{r[1]}</td>
                <td>{r[2]}</td>
                <td className="mono">{r[3]}</td>
                <td>
                  {r[4] === 'Auto-assign'
                    ? <span style={{ color: 'var(--muted)', fontSize: '.82rem' }}>Auto-assign</span>
                    : <div className="cell-user"><DashAvatar name={r[4]} size={26} /><span style={{ fontSize: '.84rem' }}>{r[4]}</span></div>}
                </td>
                <td><StatusBadge status={r[5]} /></td>
                <td className="col-actions">
                  <button>Modifier</button>
                  <button>Annuler</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </>
  )
}
